import axios from 'axios';
import getApiInstance from '../common/apiInstance';
import { getApiErrorMessage } from '../common/apiErrorMessage';
import { notifyReviewUpdated } from '../review/reviewApi';
import {
  normalizeUserCourseSettings,
  type UserCourseSettingsDto,
} from 'endchess-contracts';
import type {
  CourseDetailDto,
  CourseLineMasteryState,
  CourseListResponse,
  CourseProgressDto,
  CourseTrainModeKey,
  LessonDetailDto,
  LessonDrillMove,
  LessonDrillResultDto,
} from './types';
const coursesApi = getApiInstance('/courses', 'no-settings');

// Course catalog GETs must bypass the browser cache (304), or SPA navigation can
// reuse a stale empty list until a full page refresh.
const COURSES_GET_CONFIG = {
  headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
} as const;

const coursesGetParams = (): Record<string, number> => ({
  _: Date.now(),
});

// Share the promise for identical concurrent GETs so StrictMode double-mounts
// and sibling components produce a single backend request instead of two.
const inflight = new Map<string, Promise<unknown>>();

const dedupeInflight = <T>(key: string, run: () => Promise<T>): Promise<T> => {
  const existing = inflight.get(key);
  if (existing) {
    return existing as Promise<T>;
  }
  const promise = run().finally(() => inflight.delete(key));
  inflight.set(key, promise);
  return promise;
};

export const COURSES_UNAUTHORIZED_MESSAGE = 'Sign in to browse courses.';

export const fetchCourses = (): Promise<CourseListResponse> =>
  dedupeInflight('list', fetchCoursesUncached);

const fetchCoursesUncached = async (): Promise<CourseListResponse> => {
  try {
    const { data, status } = await coursesApi.get<CourseListResponse>('/', {
      ...COURSES_GET_CONFIG,
      params: coursesGetParams(),
      skipGlobalErrorToast: true,
      validateStatus: (s) => s === 200 || s === 401,
    });
    if (status === 401) {
      return { courses: [] };
    }
    if (!data || !Array.isArray(data.courses)) {
      throw new Error('Failed to load courses');
    }
    return data;
  } catch (error) {
    if (
      error instanceof Error &&
      error.message === COURSES_UNAUTHORIZED_MESSAGE
    ) {
      throw error;
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'load courses' }),
    );
  }
};

/** Re-sync auth and retry once when the catalog 401s but React still thinks we are signed in. */
export const fetchCoursesResilient = async (
  refreshSession: () => Promise<void>,
): Promise<CourseListResponse> => {
  try {
    return await fetchCourses();
  } catch (error) {
    if (
      !(error instanceof Error && error.message === COURSES_UNAUTHORIZED_MESSAGE)
    ) {
      throw error;
    }
  }

  await refreshSession();

  return fetchCourses();
};

export const fetchCourseBySlug = (
  slug: string,
): Promise<CourseDetailDto | null> =>
  dedupeInflight(`slug:${slug}`, () => fetchCourseBySlugUncached(slug));

const fetchCourseBySlugUncached = async (
  slug: string,
): Promise<CourseDetailDto | null> => {
  try {
    const { data, status } = await coursesApi.get<CourseDetailDto>(
      `/${encodeURIComponent(slug)}`,
      {
        ...COURSES_GET_CONFIG,
        params: coursesGetParams(),
        skipGlobalErrorToast: true,
        validateStatus: (s) => s === 200 || s === 404 || s === 401,
      },
    );
    if (status === 404 || status === 401) {
      return null;
    }
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'load course' }),
    );
  }
};

export const fetchCourseLesson = (
  slug: string,
  lessonId: string,
): Promise<LessonDetailDto | null> =>
  dedupeInflight(`lesson:${slug}:${lessonId}`, () =>
    fetchCourseLessonUncached(slug, lessonId),
  );

const fetchCourseLessonUncached = async (
  slug: string,
  lessonId: string,
): Promise<LessonDetailDto | null> => {
  try {
    const { data, status } = await coursesApi.get<LessonDetailDto>(
      `/${encodeURIComponent(slug)}/lessons/${encodeURIComponent(lessonId)}`,
      {
        ...COURSES_GET_CONFIG,
        params: coursesGetParams(),
        skipGlobalErrorToast: true,
        validateStatus: (s) => s === 200 || s === 404 || s === 401,
      },
    );
    if (status === 404 || status === 401) {
      return null;
    }
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'load lesson' }),
    );
  }
};

export const submitCourseLessonDrillResult = async (
  slug: string,
  lessonId: string,
  perMove: LessonDrillMove[],
): Promise<LessonDrillResultDto> => {
  try {
    const { data } = await coursesApi.post<LessonDrillResultDto>(
      `/${encodeURIComponent(slug)}/lessons/${encodeURIComponent(lessonId)}/drill-result`,
      { perMove },
      { skipGlobalErrorToast: true },
    );
    if (data.cardsCreated > 0 || data.cardsUpdated > 0) {
      // Refresh after the full drill batch — per-move handlers skip notify so
      // a miss followed by the answer arrow does not briefly grade the card
      // away before the final perMove batch is recorded.
      notifyReviewUpdated();
    }
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'record lesson drill' }),
    );
  }
};

export const submitCourseLineMastery = async (
  slug: string,
  lessonId: string,
  trainMode: CourseTrainModeKey,
  perMove: LessonDrillMove[],
  phase: 'drill' | 'repetition' = 'drill',
): Promise<CourseLineMasteryState> => {
  try {
    const { data } = await coursesApi.post<CourseLineMasteryState>(
      `/${encodeURIComponent(slug)}/lessons/${encodeURIComponent(lessonId)}/line-mastery`,
      { trainMode, perMove, phase },
      { skipGlobalErrorToast: true },
    );
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'save line mastery' }),
    );
  }
};

export type CourseLessonMissedPositionResult = {
  created: boolean;
  skipped: boolean;
  graded: boolean;
  failed?: boolean;
};

export const enrollCourseLessonMissedPosition = async (
  slug: string,
  lessonId: string,
  halfMove: number,
): Promise<CourseLessonMissedPositionResult> => {
  try {
    const { data, status } = await coursesApi.post<{
      created: boolean;
      skipped: boolean;
      graded: boolean;
    }>(
      `/${encodeURIComponent(slug)}/lessons/${encodeURIComponent(lessonId)}/missed-position`,
      { halfMove },
      {
        skipGlobalErrorToast: true,
        validateStatus: (s) => s === 200 || s === 201 || s === 401,
      },
    );
    if (status === 401) {
      return { created: false, skipped: false, graded: false, failed: true };
    }
    const created = data?.created === true;
    const skipped = data?.skipped === true;
    const graded = data?.graded === true;
    if (!skipped && (created || graded)) {
      notifyReviewUpdated();
    }
    return { created, skipped, graded, failed: false };
  } catch {
    return { created: false, skipped: false, graded: false, failed: true };
  }
};

export const gradeCourseLessonPosition = async (
  slug: string,
  lessonId: string,
  halfMove: number,
  isCorrect = true,
): Promise<void> => {
  try {
    await coursesApi.post<{ graded: boolean }>(
      `/${encodeURIComponent(slug)}/lessons/${encodeURIComponent(lessonId)}/replay-position/grade`,
      { halfMove, isCorrect },
      {
        skipGlobalErrorToast: true,
        validateStatus: (s) => s === 200 || s === 401,
      },
    );
  } catch {
    // Best-effort side effect of training.
  }
};

export const completeCourseLesson = async (
  slug: string,
  lessonId: string,
): Promise<CourseProgressDto> => {
  try {
    const { data } = await coursesApi.post<CourseProgressDto>(
      `/${encodeURIComponent(slug)}/progress/lessons/${encodeURIComponent(lessonId)}`,
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error('Sign in to save course progress.');
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'save lesson progress' }),
    );
  }
};

export const setCourseLessonIgnored = async (
  slug: string,
  lessonId: string,
  ignored: boolean,
): Promise<CourseProgressDto> => {
  try {
    const { data } = await coursesApi.post<CourseProgressDto>(
      `/${encodeURIComponent(slug)}/progress/lessons/${encodeURIComponent(lessonId)}/ignore`,
      { ignored },
      { skipGlobalErrorToast: true },
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error('Sign in to save course preferences.');
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'update ignored lines' }),
    );
  }
};

export const updateCourseSettings = async (
  slug: string,
  patch: Partial<UserCourseSettingsDto>,
): Promise<UserCourseSettingsDto> => {
  try {
    const { data } = await coursesApi.patch<UserCourseSettingsDto>(
      `/${encodeURIComponent(slug)}/course-settings`,
      patch,
      { skipGlobalErrorToast: true },
    );
    return normalizeUserCourseSettings(data);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new Error('Sign in to save course settings.');
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'save course settings' }),
    );
  }
};

export const excludeMistakeLesson = async (
  gameId: string,
  plyIndex: number,
): Promise<void> => {
  try {
    await coursesApi.post(
      '/mistakes/exclude',
      { gameId, plyIndex },
      { skipGlobalErrorToast: true },
    );
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'exclude mistake lesson' }),
    );
  }
};

export const clearMistakeLessonExclusion = async (
  gameId: string,
  plyIndex: number,
): Promise<void> => {
  try {
    await coursesApi.delete('/mistakes/exclude', {
      data: { gameId, plyIndex },
      skipGlobalErrorToast: true,
    });
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'restore mistake lesson' }),
    );
  }
};
